// Static UI translations for the public site (Landing + Careers).
// Job posting content (title/description/responsibilities/requirements) comes
// from the database and is authored directly — it is not translated here.

export const translations = {
  en: {
    seo: {
      home: {
        title: 'Brands Capital Group — E-commerce, Education, Logistics & Tech',
        description: 'Brands Capital Group builds and operates companies across e-commerce, education, logistics, and tech — 100+ people across four continents.',
      },
      incubator: {
        title: 'BCG Incubator | Capital, Logistics & Operating Power',
        description: 'BCG Incubator backs high-potential brands with capital, logistics, and operating power to accelerate their growth. Apply to partner with us.',
      },
      careers: {
        title: 'Careers at Brands Capital Group — Open Roles',
        description: "Join Brands Capital Group — we're hiring across e-commerce, education, logistics, and tech. Explore open roles and do the best work of your career.",
      },
    },
    nav: {
      about: 'About',
      ventures: 'Ventures',
      values: 'Values',
      careers: 'Careers',
      home: 'Home',
      projects: 'Projects',
    },
    hero: {
      title: 'Build. Operate. Dominate.',
      subtitle: 'A group across e-commerce, education, logistics, and tech. 100+ people, four continents.',
      cta: 'Discover More',
    },
    about: {
      label: 'Who We Are',
      title: "Many industries. Growth that doesn't slow down.",
      description:
        'In e-commerce, education, logistics, and software. With a team of 100+ across four continents, we move fast, execute relentlessly, and take care of the people who build with us.',
      teamMembers: 'Team members',
      divisions: 'Divisions',
      continents: 'Continents',
      revenue: 'Annual revenue',
    },
    ventures: {
      label: 'Our Divisions',
      title: 'Five divisions. One group.',
      ecommerce: { title: 'E-Commerce', desc: "Multiple brands recognized worldwide across varied industries: B2B, beauty, haircare, supplements, and more. Our expertise isn't limited to one niche — we've mastered the art of e-commerce." },
      club: { title: 'E-Commerce Capital Club', desc: 'The club that teaches you how to build a profitable, lasting e-commerce brand. The fruit of our know-how and years of experience — passed on without filter.', proof: '120+ members have generated €100K+ each.' },
      sendex: { title: 'Sendex Fulfillment', desc: 'End-to-end logistics and warehousing from China. We handle storage, shipping, and tracking for brands worldwide.', proof: '250,000+ orders fulfilled per month.' },
      saas: { title: 'SaaS', desc: 'Multiple SaaS products, B2B and B2C, serving thousands of companies and users.' },
      incubator: { title: 'BCG Incubator', desc: 'We back high-potential brands with capital, logistics, and the operating power to accelerate their growth.', apply: 'Apply to join' },
    },
    values: {
      label: 'Our Principles',
      title: 'A way of operating that sets us apart.',
      subtitle: 'We move fast, stay loyal, and play the long game.',
      loyalty: { title: 'Loyalty', desc: 'We build lasting relationships — with our teams, our partners, and our brands. Trust is our most valuable capital.' },
      speed: { title: 'Speed', desc: 'We decide fast and execute even faster.' },
      efficiency: { title: 'Efficiency', desc: 'No politics. Just results.' },
      ambition: { title: 'Ambition', desc: 'We never think small. Every business is built to become a leader in its market.' },
    },
    team: {
      label: 'The Team',
      title: '100+ team members.',
      description:
        'Working across North America, the Middle East, Europe, and China. We grow businesses — and we grow the people who run them.',
    },
    cta: {
      label: 'Careers',
      title: 'Want to join us?',
      description:
        "We're always looking for ambitious people who want to build something that matters.",
      button: 'See open roles',
    },
    footer: {
      copyright: '© 2025 Brands Capital Group',
    },
    careers: {
      heroTitle: 'Build with us.',
      heroText:
        "BCG is a team of operators building across e-commerce, education, logistics, and software. If you want to do the best work of your career, we're hiring.",
      heroCta: 'See open roles',
      openPositions: 'Open positions',
      all: 'All',
      loading: 'Loading...',
      empty: 'No open positions in this department right now.',
      viewRole: 'View role',
      openAppText: "Don't see your role? We still want to hear from exceptional people.",
      openAppLink: 'Send an open application',
      whatYoullDo: "What you'll do",
      whatLookingFor: "What we're looking for",
      apply: 'Apply',
      notFound: 'Role not found',
      notFoundText: 'This position may no longer be available.',
      backToRoles: 'Back to all roles',
    },
    incubator: {
      hero: {
        eyebrow: "BCG INCUBATOR",
        h1: "You built the brand. We help it scale.",
        sub: "We propel high-potential brands with our capital, logistics, and operating power.",
        cta: "Apply now",
      },
      what: {
        eyebrow: "WHAT IT IS",
        heading: "More than capital.",
        body: "With BCG Incubator, we open our resources to high-potential brands. Capital, infrastructure, operators: they get access to what grows our own portfolio.",
      },
      get: {
        eyebrow: "WHAT YOU GET",
        heading: "What we bring to the table.",
        c1: { t: "Capital", d: "Funding to remove the constraints that are holding your growth back." },
        c2: { t: "Logistics & fulfillment", d: "Direct access to Sendex, our China-based fulfillment center: warehousing, shipping, and fulfillment at scale." },
        c3: { t: "Operating power", d: "Our operators work alongside you on the levers that actually move the business." },
        c4: { t: "Playbooks & network", d: "The systems, frameworks, and network that grew our own brands, from zero to the top." },
        note: "Every partnership is tailored. Depending on your brand, we bring what will truly move the needle.",
      },
      who: {
        eyebrow: "WHO IT'S FOR",
        heading: "Built for brands that have hit a ceiling.",
        body: "BCG Incubator is for established brands with proven demand that have hit a ceiling. If you've built something that works but can't scale it alone — capital, supply chain, operations, or all three — that's exactly where we come in. It's not for pre-launch ideas or brands still searching for product-market fit.",
      },
      how: {
        eyebrow: "HOW IT WORKS",
        heading: "From application to partnership.",
        s1: { t: "Apply", d: "Tell us about your brand and where you want to take it. Five minutes." },
        s2: { t: "Review", d: "If there's a fit, we dig into the numbers and the opportunity together." },
        s3: { t: "Partner", d: "We structure a partnership around your brand and get to work." },
      },
      apply: {
        eyebrow: "APPLY",
        heading: "Apply to the BCG",
        line: "Takes about five minutes. If there's a fit, we'll be in touch.",
      },
    },
  },

  fr: {
    seo: {
      home: {
        title: 'Brands Capital Group — E-commerce, Éducation, Logistique & Tech',
        description: "Brands Capital Group construit et opère des entreprises dans l'e-commerce, l'éducation, la logistique et la tech — plus de 100 personnes sur quatre continents.",
      },
      incubator: {
        title: 'BCG Incubator | Capital, logistique et puissance opérationnelle',
        description: "BCG Incubator propulse les marques à fort potentiel avec du capital, de la logistique et une puissance opérationnelle pour accélérer leur croissance.",
      },
      careers: {
        title: 'Carrières chez Brands Capital Group — Postes ouverts',
        description: "Rejoignez Brands Capital Group — nous recrutons dans l'e-commerce, l'éducation, la logistique et la tech. Découvrez nos postes ouverts.",
      },
    },
    nav: {
      about: 'À propos',
      ventures: 'Activités',
      values: 'Valeurs',
      careers: 'Carrières',
      home: 'Accueil',
      projects: 'Projets',
    },
    hero: {
      title: 'Construire. Opérer. Dominer.',
      subtitle: "Une organisation établie dans l'e-commerce, l'éducation, la logistique et la tech, sur quatre continents.",
      cta: 'En savoir plus',
    },
    about: {
      label: 'Qui sommes-nous',
      title: 'Plusieurs industries. Une croissance qui ne ralentit pas.',
      description:
        "Implantés dans l'e-commerce, l'éducation, la logistique et la tech. Avec une équipe de plus de 100 personnes réparties sur quatre continents, nous avançons vite, exécutons sans relâche et prenons soin de ceux qui construisent avec nous.",
      teamMembers: 'Collaborateurs',
      divisions: 'Pôles',
      continents: 'Continents',
      revenue: "Chiffre d'affaires annuel",
    },
    ventures: {
      label: 'Nos pôles',
      title: 'Cinq pôles. Un groupe.',
      ecommerce: { title: 'E-Commerce', desc: "Plusieurs marques reconnues mondialement, dans des industries variées : B2B, beauté, soin capillaire, compléments alimentaires, etc. Notre expertise ne se limite pas à une seule niche : nous avons maîtrisé l'art de l'e-commerce." },
      club: { title: 'E-Commerce Capital Club', desc: "Le club qui enseigne comment construire une marque e-commerce rentable et durable. Le fruit de notre savoir-faire et de nos années d'expérience, transmis sans filtre.", proof: '120+ membres ont généré plus de 100K€ chacun.' },
      sendex: { title: 'Sendex Fulfillment', desc: "Logistique et entreposage de bout en bout depuis la Chine. Nous gérons le stockage, l'expédition et le suivi pour des marques du monde entier.", proof: '250 000+ commandes traitées par mois.' },
      saas: { title: 'SaaS', desc: "Plusieurs logiciels SaaS, B2B et B2C, au service de milliers d'entreprises et d'utilisateurs." },
      incubator: { title: 'BCG Incubator', desc: 'Nous soutenons des marques à fort potentiel avec du capital, de la logistique et la puissance opérationnelle pour accélérer leur croissance.', apply: 'Candidater' },
    },
    values: {
      label: 'Nos principes',
      title: 'Une façon de faire qui nous distingue.',
      subtitle: 'Nous avançons vite, restons loyaux, et jouons sur le long terme.',
      loyalty: { title: 'Loyauté', desc: "Nous construisons des relations durables avec nos équipes et nos partenaires. La confiance est notre capital le plus précieux." },
      speed: { title: 'Rapidité', desc: "Nous avons conscience de l'importance de la rapidité. Nous décidons vite et exécutons encore plus vite." },
      efficiency: { title: 'Efficacité', desc: "Malgré notre taille, nous restons efficaces. Pas de friction, pas de processus inutiles. Juste de l'exécution." },
      ambition: { title: 'Ambition', desc: "Nous ne pensons jamais petit. Chaque entreprise est conçue pour devenir un leader sur son marché." },
    },
    team: {
      label: "L'équipe",
      title: "Plus de 100 membres d'équipe.",
      description:
        "Présents en Amérique du Nord, au Moyen-Orient, en Europe et en Chine. Nous faisons croître des entreprises — et les personnes qui les dirigent.",
    },
    cta: {
      label: 'Carrières',
      title: 'Envie de nous rejoindre ?',
      description:
        "Nous recherchons en permanence des personnes talentueuses qui veulent bâtir des choses qui ont un vrai impact.",
      button: 'Voir les postes ouverts',
    },
    footer: {
      copyright: '© 2025 Brands Capital Group',
    },
    careers: {
      heroTitle: 'Travaillez avec nous.',
      heroText:
        "BCG est une équipe d'opérateurs qui bâtit dans l'e-commerce, l'éducation, la logistique et le logiciel. Si vous voulez réaliser le meilleur travail de votre carrière, nous recrutons.",
      heroCta: 'Voir les postes ouverts',
      openPositions: 'Postes ouverts',
      all: 'Tous',
      loading: 'Chargement...',
      empty: 'Aucun poste ouvert dans ce département pour le moment.',
      viewRole: 'Voir le poste',
      openAppText: 'Vous ne voyez pas votre poste ? Nous voulons tout de même entendre les personnes exceptionnelles.',
      openAppLink: 'Envoyer une candidature spontanée',
      whatYoullDo: 'Vos missions',
      whatLookingFor: 'Profil recherché',
      apply: 'Postuler',
      notFound: 'Poste introuvable',
      notFoundText: "Ce poste n'est peut-être plus disponible.",
      backToRoles: 'Retour à tous les postes',
    },
    incubator: {
      hero: {
        eyebrow: "BCG INCUBATOR",
        h1: "Vous avez créé la marque. Nous l'aidons à changer de dimension.",
        sub: "Nous propulsons les marques à fort potentiel avec notre capital, logistique et puissance opérationnelle.",
        cta: "Candidater",
      },
      what: {
        eyebrow: "CE QUE C'EST",
        heading: "Plus que du capital.",
        body: "Avec BCG Incubator, nous ouvrons nos ressources à des marques à fort potentiel. Capital, infrastructure, opérateurs : elles accèdent à ce qui fait grandir notre propre portefeuille.",
      },
      get: {
        eyebrow: "CE QUE VOUS OBTENEZ",
        heading: "Ce que nous apportons.",
        c1: { t: "Capital", d: "Du financement pour lever les freins qui bloquent votre croissance." },
        c2: { t: "Logistique & fulfillment", d: "Un accès privilégié à Sendex, notre centre de logistique en Chine : entreposage, expédition et traitement des commandes à grande échelle." },
        c3: { t: "Force opérationnelle", d: "Nos opérateurs travaillent à vos côtés sur les leviers qui font réellement avancer l'entreprise." },
        c4: { t: "Méthodes & réseau", d: "Les systèmes, les frameworks et le réseau qui ont fait grandir nos propres marques, de zéro jusqu'au sommet." },
        note: "Chaque partenariat est construit sur mesure. Selon votre marque, nous apportons ce qui fera vraiment la différence.",
      },
      who: {
        eyebrow: "POUR QUI",
        heading: "Conçu pour les marques qui ont atteint un plafond.",
        body: "BCG Incubator s'adresse aux marques établies, à la demande prouvée, qui ont atteint un plafond. Si vous avez créé quelque chose qui fonctionne mais que vous ne pouvez pas faire grandir seul — capital, chaîne d'approvisionnement, opérations, ou les trois — c'est exactement là que nous intervenons. Ce n'est pas fait pour les projets pré-lancement ni pour les marques encore à la recherche de leur product-market fit.",
      },
      how: {
        eyebrow: "COMMENT ÇA MARCHE",
        heading: "De la candidature au partenariat.",
        s1: { t: "Candidater", d: "Parlez-nous de votre marque et de là où vous voulez l'emmener. Cinq minutes." },
        s2: { t: "Étude", d: "Si le potentiel est là, nous examinons ensemble les chiffres et l'opportunité." },
        s3: { t: "Partenariat", d: "Nous structurons un partenariat autour de votre marque et nous nous mettons au travail." },
      },
      apply: {
        eyebrow: "CANDIDATER",
        heading: "Candidater au BCG",
        line: "Environ cinq minutes. Si le potentiel est là, nous vous recontactons.",
      },
    },
  },
};
